import { interpolate } from '@popmotion/popcorn';
import classNames from 'classnames/bind';
import { motion, useViewportScroll, useMotionValue, useSpring } from 'framer-motion';
import React from 'react';

import Heading from 'components/shared/heading';
import useDeviceType, { DEVICE_TYPE } from 'hooks/use-device-type';
import useSectionOffset from 'hooks/use-section-offset';
import Adidas from 'images/pages/home/stores/adidas-logo.inline.svg';
import Champs from 'images/pages/home/stores/champs-logo.inline.svg';
import Dicks from 'images/pages/home/stores/dicks-logo.inline.svg';
import Eastbay from 'images/pages/home/stores/eastbay-logo.inline.svg';
import Footaction from 'images/pages/home/stores/footaction-logo.inline.svg';
import Hibbett from 'images/pages/home/stores/hibbett-logo.inline.svg';
import Jd from 'images/pages/home/stores/jd-logo.inline.svg';
import JimmyJazz from 'images/pages/home/stores/jimmyjazz-logo.inline.svg';
import Overkill from 'images/pages/home/stores/overkill-logo.inline.svg';
import Supreme from 'images/pages/home/stores/supreme-logo.inline.svg';
import Titolo from 'images/pages/home/stores/titolo-logo.inline.svg';
import Wellgosh from 'images/pages/home/stores/wellgosh-logo.inline.svg';

import styles from './stores.module.scss';

const cx = classNames.bind(styles);

const LOGOS = [
  [Footaction, Adidas, Champs, Eastbay, Titolo, Wellgosh],
  [Supreme, Hibbett, Dicks, Jd, Overkill, JimmyJazz],
];

const LOGOS_MD = [
  [Footaction, Adidas, Champs, Eastbay],
  [Titolo, Wellgosh, Supreme, Hibbett],
  [Dicks, Jd, Overkill, JimmyJazz],
];

const springOpts = {
  damping: 30,
};

// percentage of section height
const SCROLL_VARIANTS = [0, 1];
const CARDS_X_VARIANTS_REM_LG = [
  [
    [0, -146.25],
    [146.25, 0],
  ],
  [
    [-11.25, 135],
    [-157.5, -11.25],
  ],
];

const CARDS_X_VARIANTS_REM_MD = [
  [
    [0, -124],
    [124, 0],
  ],
  [
    [-14.25, 109.75],
    [-138.25, -14.25],
  ],
];

const CARDS_X_VARIANTS_REM_SM = [
  [
    [0, -232],
    [232, 0],
  ],
  [
    [-26.575, 205.425],
    [-258.575, -26.575],
  ],
];

const isOnClient = typeof window !== 'undefined';

const Stores = () => {
  const sectionRef = React.useRef();
  const { scrollY } = useViewportScroll();

  const deviceType = useDeviceType();

  const [sectionStart, sectionEnd, sectionHidden] = useSectionOffset(sectionRef);

  const scrollRange = SCROLL_VARIANTS.map(
    (input) => sectionStart + (sectionHidden - sectionStart) * input
  );

  const remToPx = (rem) => {
    if (isOnClient) {
      return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
    }
    return 0;
  };

  let CARDS_X_VARIANTS_REM = CARDS_X_VARIANTS_REM_LG;
  if (deviceType === DEVICE_TYPE.tablet.portrait) {
    CARDS_X_VARIANTS_REM = CARDS_X_VARIANTS_REM_MD;
  }

  if (deviceType === DEVICE_TYPE.mobile.portrait) {
    CARDS_X_VARIANTS_REM = CARDS_X_VARIANTS_REM_SM;
  }

  const interpolator00 = interpolate(scrollRange, CARDS_X_VARIANTS_REM[0][0].map(remToPx));
  const interpolator01 = interpolate(scrollRange, CARDS_X_VARIANTS_REM[0][1].map(remToPx));
  const interpolator10 = interpolate(scrollRange, CARDS_X_VARIANTS_REM[1][0].map(remToPx));
  const interpolator11 = interpolate(scrollRange, CARDS_X_VARIANTS_REM[1][1].map(remToPx));

  const x00 = useMotionValue(remToPx(CARDS_X_VARIANTS_REM[0][0][0]));
  x00.set(interpolator00(scrollY.get()));
  const value00 = useSpring(x00, springOpts);

  const x01 = useMotionValue(remToPx(CARDS_X_VARIANTS_REM[0][1][0]));
  x01.set(interpolator01(scrollY.get()));
  const value01 = useSpring(x01, springOpts);

  const x10 = useMotionValue(remToPx(CARDS_X_VARIANTS_REM[1][0][0]));
  x10.set(interpolator01(scrollY.get()));
  const value10 = useSpring(x10, springOpts);

  const x11 = useMotionValue(remToPx(CARDS_X_VARIANTS_REM[1][1][0]));
  x11.set(interpolator01(scrollY.get()));
  const value11 = useSpring(x11, springOpts);

  React.useEffect(() => {
    const unsub = scrollY.onChange(() => {
      const currentScrollY = scrollY.get();

      x00.set(interpolator00(currentScrollY));
      x01.set(interpolator01(currentScrollY));
      x10.set(interpolator10(currentScrollY));
      x11.set(interpolator11(currentScrollY));
    });

    return () => {
      unsub();
    };
    // need use scroll positions as dependencies to rerender and update interpolation range when correct scroll offsets are available
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionStart, sectionHidden, isOnClient, deviceType]);

  return (
    <section id="stores" className={cx('container', 'wrapper')} ref={sectionRef}>
      <Heading className={cx('title')} size="lg" tag="h2" theme="primary">
        Supported stores
      </Heading>
      <p className={cx('subtitle')}>
        We support the most common stores like the
        {deviceType !== DEVICE_TYPE.mobile.portrait && <br />}{' '}
        <span className={cx('store')}>FinishLine</span>,{' '}
        <span className={cx('store')}>FootLocker</span> and{' '}
        <span className={cx('store')}>YeezySupply</span>. And other:
      </p>
      {(deviceType === DEVICE_TYPE.desktop ||
        deviceType === DEVICE_TYPE.tablet.landscape ||
        deviceType === DEVICE_TYPE.mobile.landscape) && (
        <>
          <div className={cx('cards-row')}>
            <motion.div className={cx('cards-row-inner')} style={{ x: value00 }}>
              {LOGOS[0].map((Logo, i) => (
                <div className={cx('card')} key={i}>
                  <Logo />
                </div>
              ))}
            </motion.div>
            <motion.div className={cx('cards-row-inner')} style={{ x: value01 }}>
              {LOGOS[0].map((Logo, i) => (
                <div className={cx('card')} key={i}>
                  <Logo />
                </div>
              ))}
            </motion.div>
          </div>
          <div className={cx('cards-row')}>
            <motion.div className={cx('cards-row-inner')} style={{ x: value10 }}>
              {LOGOS[1].map((Logo, i) => (
                <div className={cx('card')} key={i}>
                  <Logo />
                </div>
              ))}
            </motion.div>
            <motion.div className={cx('cards-row-inner')} style={{ x: value11 }}>
              {LOGOS[1].map((Logo, i) => (
                <div className={cx('card')} key={i}>
                  <Logo />
                </div>
              ))}
            </motion.div>
          </div>
        </>
      )}

      {(deviceType === DEVICE_TYPE.tablet.portrait ||
        deviceType === DEVICE_TYPE.mobile.portrait) && (
        <>
          <div className={cx('cards-row')}>
            <motion.div className={cx('cards-row-inner')} style={{ x: value00 }}>
              {LOGOS_MD[0].map((Logo, i) => (
                <div className={cx('card')} key={i}>
                  <Logo />
                </div>
              ))}
            </motion.div>
            <motion.div className={cx('cards-row-inner')} style={{ x: value01 }}>
              {LOGOS_MD[0].map((Logo, i) => (
                <div className={cx('card')} key={i}>
                  <Logo />
                </div>
              ))}
            </motion.div>
          </div>
          <div className={cx('cards-row')}>
            <motion.div className={cx('cards-row-inner')} style={{ x: value10 }}>
              {LOGOS_MD[1].map((Logo, i) => (
                <div className={cx('card')} key={i}>
                  <Logo />
                </div>
              ))}
            </motion.div>
            <motion.div className={cx('cards-row-inner')} style={{ x: value11 }}>
              {LOGOS_MD[1].map((Logo, i) => (
                <div className={cx('card')} key={i}>
                  <Logo />
                </div>
              ))}
            </motion.div>
          </div>
          <div className={cx('cards-row')}>
            <motion.div className={cx('cards-row-inner')} style={{ x: value00 }}>
              {LOGOS_MD[2].map((Logo, i) => (
                <div className={cx('card')} key={i}>
                  <Logo />
                </div>
              ))}
            </motion.div>
            <motion.div className={cx('cards-row-inner')} style={{ x: value01 }}>
              {LOGOS_MD[2].map((Logo, i) => (
                <div className={cx('card')} key={i}>
                  <Logo />
                </div>
              ))}
            </motion.div>
          </div>
        </>
      )}
      <div className={cx('illustration')} />
    </section>
  );
};

export default Stores;
